import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  View,
} from "@/components/ui";
import { IconButton } from "@/components/ui/icon-button";
import type { RescheduleVisitWorkerType } from "@/store/visits/visitDetail";
import { useVisitDetailStore } from "@/store/visits/visitDetail";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { getWorkerLabel } from "@/lib/service/helpers";
import { isReadyToSchedule } from "@/lib/booking-lib";
import type { PackageServiceTypeEnum } from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  VisitStatusEnum,
} from "@/__generated__/graphql";
import { useRoute } from "@/components/shared/router";
import { addMonths } from "date-fns";
import { formatDate } from "@/lib/helpers/date";
import { InfoDialog } from "@/components/shared/info-dialog";
import { Typography } from "@/components/shared/typography";
import { IfElse } from "@/components/shared/if-else";
import {
  OfflineBookingServiceTypes,
  OnlineBookingDepartments,
} from "@/types/service";
import { onChatSales } from "@/lib/chat-sales";

export function RescheduleSkipVisitConfirmation({
  visitId,
  department = PackageDepartmentEnum.HomeCleaning,
  visitStatus,
  serviceDate,
  hasWorkerAssignment,
  serviceType,
  onClickSkipVisit,
  skipVisitButtonLoading,
}: {
  visitId: string;
  department?: PackageDepartmentEnum;
  visitStatus: VisitStatusEnum;
  serviceDate: Date;
  hasWorkerAssignment: boolean;
  serviceType: PackageServiceTypeEnum;
  onClickSkipVisit: () => void;
  skipVisitButtonLoading?: boolean;
}) {
  const showConfirmation = useVisitDetailStore(
    (state) => state.data.showRescheduleVisitConfirmation,
  );

  const onCloseRescheduleVisitConfirmation = useVisitDetailStore(
    (state) => state.closeRescheduleVisitConfirmation,
  );

  const onCloseSkipVisitConfirmationModal = useVisitDetailStore(
    (state) => state.closeSkipVisitConfirmationModal,
  );

  const handleClose = () => {
    onCloseRescheduleVisitConfirmation();
    onCloseSkipVisitConfirmationModal();
  };
  const setRescheduleWorkerType = useVisitDetailStore(
    (state) => state.setRescheduleVisitWorkerType,
  );
  const setVisitDepartment = useVisitDetailStore(
    (state) => state.setVisitDepartment,
  );
  const visitToken = useVisitDetailStore((state) => state.data.visitToken);

  const { push } = useRoute();

  const { isDesktop } = useWindowDimensions();

  const cleanerLabel = getWorkerLabel(department);

  const handleReschedule = (type: RescheduleVisitWorkerType) => () => {
    setRescheduleWorkerType(type);
    setVisitDepartment(department);
    handleClose();
    let query: { id: string } | { token: string } = {
      id: visitId,
    };

    if (visitToken) {
      query = {
        token: visitToken,
      };
    }

    push({
      pageKey: "rescheduleVisit",
      query,
    });
  };

  if (
    visitStatus === VisitStatusEnum.PendingClientSchedule &&
    !isReadyToSchedule(serviceDate)
  ) {
    return (
      <InfoDialog
        open
        title="Reschedule Visit"
        subtitle={`You can reschedule this visit after ${formatDate(
          addMonths(serviceDate, -1),
          "dd/MM/yyyy",
        )}`}
        onConfirm={handleClose}
        buttonText="Got it, thanks!"
      />
    );
  }

  const onContactProfessional = (id: string) => {
    onChatSales(
      true,
      `Hi! I would like to speak to sales about rescheduling my appointment ${id}`,
    );
  };

  const confirmationMassage = `Reschedule this visit with either your current ${cleanerLabel} (subject to availability) or choose any ${cleanerLabel} for the greatest flexibility!`;

  if (showConfirmation) {
    return (
      <BottomDrawerModal open onOpenChange={handleClose}>
        <Dialog>
          <DialogContent>
            <DialogHeader>
              <View className="relative flex flex-row items-center justify-between gap-2">
                <DialogTitle>Reschedule</DialogTitle>
                {isDesktop && (
                  <IconButton
                    variant="ghost"
                    color="foreground"
                    size="md"
                    iconName="x"
                    onClick={handleClose}
                  />
                )}
              </View>
            </DialogHeader>
          </DialogContent>
        </Dialog>
        <View className="flex flex-col gap-2">
          <View className="flex flex-col pb-1 md:gap-4 md:pb-4">
            <Typography>Skip Visit</Typography>
            <Typography variant="body-lg">{confirmationMassage}</Typography>
          </View>
          <IfElse
            if={
              OnlineBookingDepartments.includes(department) &&
              !OfflineBookingServiceTypes.includes(serviceType)
            }
            else={
              <Button
                variant="secondary"
                color="success"
                key="buttonContactProfessional"
                fullWidth="full"
                size="md"
                iconSize="md"
                onClick={() => onContactProfessional(visitId)}
                iconName="whatsappIcon"
                iconColor="fill-success"
                children="Reschedule"
              />
            }
          >
            <>
              {hasWorkerAssignment && (
                <Button
                  variant="primary"
                  color="CTA"
                  fullWidth="full"
                  onClick={handleReschedule("CURRENT_WORKER")}
                  children={`Reschedule with my Current ${cleanerLabel}`}
                />
              )}

              <Button
                variant="secondary"
                color="CTA2"
                fullWidth="full"
                onClick={handleReschedule("ANY_WORKER")}
                children={`Reschedule with Any ${cleanerLabel}`}
              />
            </>
          </IfElse>
          <Button
            variant="tertiary"
            color="CTA2"
            onClick={onClickSkipVisit}
            children="No thanks, I'd like to skip my visit"
            loading={skipVisitButtonLoading}
          />
        </View>
      </BottomDrawerModal>
    );
  }

  return null;
}
